import type { IServerSideDatasource } from "ag-grid-community";
import dayjs from "dayjs";
import React, { type ComponentPropsWithoutRef } from "react";

import { trpc } from "@blis/trpc/react";

import { AgGridReact } from "@/components/grid/AgGrid";
import { Link } from "@/components/Link";
import { Badge } from "@/components/ui/badge";
import { linkStyles } from "@/components/ui/link";
import { useAdminServerSideGrid } from "@/features/admin/utils/useAdminServerSideGrid";
import { getApiUrl } from "@/lib/api";

export function NotificationsGrid() {
  const utils = trpc.useUtils();

  const serverSide: IServerSideDatasource = React.useMemo(
    () => ({
      getRows: async (params) => {
        const { startRow, endRow, filterModel, sortModel } = params.request;
        // console.log({ startRow, endRow, filterModel });
        try {
          const { data, totalCount } = await utils.notifications.list.fetch({
            take: (endRow ?? 0) - (startRow ?? 0),
            skip: startRow ?? 0,
            // biome-ignore lint/suspicious/noExplicitAny: TODO
            filterModel: filterModel as any,
            // biome-ignore lint/suspicious/noExplicitAny: TODO
            sortModel: sortModel as any,
          });
          params.success({
            rowData: data,
            rowCount: totalCount,
          });
        } catch (_err) {
          params.fail();
          return;
        }
      },
    }),
    [utils],
  );
  const { gridProps } = useAdminServerSideGrid<Record<string, unknown>>({
    serverSideDatasource: serverSide,
    paginationPageSize: 100,
    paginationPageSizeSelector: [10, 25, 50, 100, 500, 1000, 5000],
  });

  return (
    <div className="ag-theme-quartz ag-theme-blis ag-theme-blis-full h-full w-full">
      <AgGridReact
        {...gridProps}
        // rowData={results}
        // animateRows
        defaultColDef={{
          sortable: false,
          filterParams: {
            maxNumConditions: 1,
          },
        }}
        columnDefs={[
          {
            field: "createdAt",
            filter: "agDateColumnFilter",
            filterParams: {
              filterOptions: ["equals", "lessThan", "greaterThan", "inRange"],
            },
            sortable: true,
            initialSort: "desc",
            valueFormatter: ({ value }) =>
              dayjs(value).format("YYYY-MM-DD HH:mm"),
          },

          {
            field: "type",
            headerName: "Type",
            filter: "agSetColumnFilter",
            filterParams: {
              values: ["report", "received"],
            },
            cellRenderer: ({ value }) => <pre>{value}</pre>,
          },
          {
            field: "status",
            headerName: "Status",
            filter: "agSetColumnFilter",
            filterParams: {
              values: ["SUCCESS", "FAILED", "PENDING", "SKIPPED", "QUEUED"],
            },
            width: 120,
            minWidth: 120,
            valueFormatter: ({ value }) => {
              return value?.toUpperCase();
            },
            cellRenderer: StatusBadge,
          },
          {
            field: "recipient",
            filter: "agTextColumnFilter",
          },
          {
            field: "accessionId",
            headerName: "Accession",
            width: 140,
            filter: "agTextColumnFilter",
            sortable: true,
            cellRenderer: AccessionLink,
          },
          {
            field: "org",
            headerName: "Client",
            filter: "agSetColumnFilter",
            filterParams: {
              values: async (params) => {
                const orgs = await utils.orgs.list.fetch();
                params.success(orgs.map((org) => org.name));
              },
            },
          },
          {
            field: "reportPath",
            headerName: "Report",
            cellRenderer: ReportLink,
          },
        ]}
        sideBar={{
          toolPanels: ["columns", "filters"],
        }}
      />
    </div>
  );
}

const AccessionLink = React.memo(({ value }: { value: string }) => (
  <Link to="/accessions/$id" params={{ id: value }}>
    {value}
  </Link>
));

const StatusBadge = React.memo(({ value }: { value: string }) => {
  let color: ComponentPropsWithoutRef<typeof Badge>["color"] = "gray";
  switch (value) {
    case "SUCCESS":
      color = "green";
      break;
    case "FAILED":
      color = "red";
      break;
    case "PENDING":
      color = "yellow";
      break;
    case "SKIPPED":
      color = "gray";
      break;
    case "QUEUED":
      color = "blue";
      break;
  }
  return (
    <div className="flex h-full items-center">
      <Badge color={color}>{value}</Badge>
    </div>
  );
});

const ReportLink = React.memo(
  ({ value, data }: { value: string; data: { reportId: number } }) => {
    return (
      <a
        href={getApiUrl(`/api/data/reports/${value}`)}
        target="_blank"
        rel="noreferrer"
        className={linkStyles()}
      >
        {data.reportId}
      </a>
    );
  },
);
